/**
 * The wire contract, and every data type that crosses it.
 *
 * Split out of `./snapshot.ts` in D11 for one reason: this module imports
 * nothing from Node. `apps/site` is a static browser bundle with no SQLite and
 * no `node:fs`, but it renders the same rows `apps/web` renders and must agree
 * with the publisher about their shape down to the field. Two copies of these
 * interfaces would drift the first time a column was added; one cannot.
 *
 * So the rule for this file is narrow and worth keeping: pure TypeScript and
 * pure functions only. Anything that needs a database handle belongs next door
 * in `./snapshot.ts`, which re-exports everything here so existing importers
 * see no difference.
 */

/** Rows a caller sees without an entitlement. The mockup's free tier caps here. */
export const FREE_ROW_LIMIT = 7;

/** Past this, the agent is considered stopped. */
export const TICK_STALE_MS = 10 * 60 * 1000;

/** Past this, a published snapshot is stale and /api/health fails. */
export const SNAPSHOT_STALE_MS = 10 * 60 * 1000;

export type BadgeKind = 'gap' | 'dis' | 'whale' | 'launch' | 'thin' | 'flat';

export interface Badge {
  kind: BadgeKind;
  label: string;
}

export interface SnapshotRow {
  rank: number;
  poolAddress: string;
  /** Uppercased for display; the chain carries some tickers lowercase. */
  symbol: string;
  name: string | null;
  /** Quote asset, e.g. "USDG". Two pools of one token differ only by this. */
  quote: string | null;
  priceUsd: number | null;
  lastClose: number | null;
  closeDate: string | null;
  /**
   * What `premiumPct` is measured against: the live Chainlink reference while
   * NYSE is open (when a fresh one exists), otherwise the last stored close.
   */
  refKind: 'chainlink' | 'close';
  /** The reference price itself, in USD. */
  refUsd: number | null;
  /** On-chain price against `refUsd`, in percent. Null when the price is stale. */
  premiumPct: number | null;
  vol24hUsd: number | null;
  liqUsd: number | null;
  trades24h: number;
  score: number;
  /** Newest tick for the pool, even when it is too old to show as a price. */
  lastTickTs: number | null;
  /** Up to 24 price points over the last 24h, oldest first. */
  spark: number[];
  badge: Badge;
  /** fomo.family page for the verified stock-token contract (optional; added 28 Sep). */
  fomoUrl?: string | null;
}

export interface VolumeBar {
  startTs: number;
  endTs: number;
  usd: number;
  trades: number;
  /** 0..1 against the tallest bar in the window. */
  height: number;
}

export interface SignalRow {
  id: string;
  type: string;
  symbol: string | null;
  ts: number;
  severity: string | null;
  state: string;
  payload_json: string | null;
}

/** A signal reduced to what a feed row or an alert rail row renders. */
export interface AlertItem {
  id: string;
  type: string;
  symbol: string | null;
  ts: number;
  severity: string | null;
  headline: string;
  subline: string | null;
  /** fomo.family page carried in the signal's payload, when it has one. */
  fomo?: string | null;
}

export interface LandingStats {
  /**
   * Signals published to a public channel (X or Telegram). Drafts do not
   * count: a landing page that says "posted" about a draft is saying something
   * that did not happen (desk QA, 28 Sep).
   */
  alertsPosted: number | null;
  /** The same count restricted to the last 24h, for the tile's delta line. */
  alertsPosted24h: number | null;
  /** Every signal the engine raised, whatever became of it. */
  signalsRaised: number | null;
  /** The same count restricted to the last 24h. */
  signalsRaised24h: number | null;
  /** Distinct stock tokens in `tokens`, whatever their pool coverage. */
  stockTokens: number | null;
  /** Sum of recorded treasury sweeps. Null while no sweep has been recorded. */
  treasuryUsd: number | null;
  /** Sweeps recorded in the last 7 days. Null while none has been recorded. */
  sweptThisWeekUsd: number | null;
}

export interface Allocation {
  symbol: string;
  usd: number;
  /** Share of the treasury total, 0..1. */
  share: number;
}

export interface Treasury {
  total: number | null;
  allocations: Allocation[];
  lastSweepTs: number | null;
  sweepCount: number;
}

export interface HonestNumber {
  label: string;
  value: number | null;
  /** What the number is counted from, so a reader can check it themselves. */
  source: string;
  /** Epoch ms of the newest row the number is derived from, when one exists. */
  asOf: number | null;
  note?: string;
}

export interface SweepRecord {
  id: string;
  ts: number;
  txHash: string | null;
  symbol: string | null;
  usd: number | null;
  note: string | null;
}

export type PostMode = 'draft' | 'live';

/** The liveness facts that come out of the database, host details excluded. */
export interface HealthFacts {
  lastTickTs: number | null;
  lastRefTs: number | null;
  lastSignalTs: number | null;
  xPostsToday: number;
}

export const EMPTY_LANDING: LandingStats = {
  alertsPosted: null,
  alertsPosted24h: null,
  signalsRaised: null,
  signalsRaised24h: null,
  stockTokens: null,
  treasuryUsd: null,
  sweptThisWeekUsd: null,
};

export const EMPTY_TREASURY: Treasury = {
  total: null,
  allocations: [],
  lastSweepTs: null,
  sweepCount: 0,
};

export const EMPTY_HEALTH_FACTS: HealthFacts = {
  lastTickTs: null,
  lastRefTs: null,
  lastSignalTs: null,
  xPostsToday: 0,
};

interface PayloadShape {
  headline?: unknown;
  subline?: unknown;
  tag?: unknown;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Captions come from the poster's payload. A signal that never reached the
 * caption stage still has to render, so the type and symbol are the fallback —
 * never invented copy.
 */
/** Only a fomo.family URL passes; anything else in the payload is ignored. */
function fomoOf(payload: PayloadShape): string | null {
  const links = (payload as { links?: { fomo?: unknown } }).links;
  const f = links?.fomo;
  return typeof f === 'string' && f.startsWith('https://fomo.family/tokens/robinhood/0x') ? f : null;
}

export function toAlertItem(r: SignalRow): AlertItem {
  let payload: PayloadShape = {};
  if (r.payload_json) {
    try {
      payload = JSON.parse(r.payload_json) as PayloadShape;
    } catch {
      payload = {};
    }
  }
  const fallback = r.symbol ? `${r.symbol.toUpperCase()} ${r.type}` : r.type;
  return {
    id: r.id,
    type: r.type,
    symbol: r.symbol,
    ts: r.ts,
    severity: r.severity,
    headline: str(payload.headline) ?? fallback,
    subline: str(payload.subline) ?? str(payload.tag),
    fomo: fomoOf(payload),
  };
}
/** A signal from the last 24h, with the rendered card if one was published. */
export interface PublishedSignal extends AlertItem {
  /** Public URL of the rendered card PNG, or null when there is none. */
  cardUrl: string | null;
}
export interface PublishedHealth extends HealthFacts {
  postMode: PostMode;
  dbBytes: number | null;
}
/**
 * The wire object the agent writes to `data/snapshot/latest.json` and uploads
 * to S3, and the only thing the Amplify site reads.
 *
 * Rows are the FULL set. The entitlement slice belongs to whoever answers the
 * request, not to the publisher — an Amplify instance serving a paying caller
 * needs the rows a free caller must not see, and it cannot go back to the
 * database for them.
 */
export interface PublishedSnapshot {
  version: 1;
  generatedAt: number;
  generatedAtIso: string;
  rows: SnapshotRow[];
  totalPools: number;
  trackedTokens: number;
  freeRowLimit: number;
  feed: AlertItem[];
  volume12h: VolumeBar[];
  dataAsOf: number | null;
  signals24h: PublishedSignal[];
  honestNumbers: HonestNumber[];
  landing: LandingStats;
  treasury: Treasury;
  latestAlerts: AlertItem[];
  sweeps: SweepRecord[];
  signalCounts24h: Record<string, number>;
  health: PublishedHealth;
}
function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Parse a published snapshot, or return null.
 *
 * Deliberately lenient about everything except the two fields the site cannot
 * fake: the version and `generatedAt`. A snapshot missing a section renders as
 * the empty state for that section, which is the same thing an empty database
 * produces — far better than a 500 across the whole site because one array was
 * absent. A snapshot with no honest `generatedAt` is rejected outright, because
 * without it `/api/health` would report a freshness it cannot know.
 */
export function parsePublishedSnapshot(input: unknown): PublishedSnapshot | null {
  let o: unknown = input;
  if (typeof input === 'string') {
    try {
      o = JSON.parse(input);
    } catch {
      return null;
    }
  }
  if (typeof o !== 'object' || o === null) return null;
  const s = o as Record<string, unknown>;
  if (s['version'] !== 1) return null;
  const generatedAt = num(s['generatedAt']);
  if (generatedAt === null) return null;

  const health = (typeof s['health'] === 'object' && s['health'] !== null
    ? (s['health'] as Record<string, unknown>)
    : {}) as Record<string, unknown>;

  return {
    version: 1,
    generatedAt,
    generatedAtIso:
      typeof s['generatedAtIso'] === 'string'
        ? s['generatedAtIso']
        : new Date(generatedAt).toISOString(),
    rows: arr<SnapshotRow>(s['rows']),
    totalPools: num(s['totalPools']) ?? arr(s['rows']).length,
    trackedTokens: num(s['trackedTokens']) ?? 0,
    freeRowLimit: num(s['freeRowLimit']) ?? FREE_ROW_LIMIT,
    feed: arr<AlertItem>(s['feed']),
    volume12h: arr<VolumeBar>(s['volume12h']),
    dataAsOf: num(s['dataAsOf']),
    signals24h: arr<PublishedSignal>(s['signals24h']),
    honestNumbers: arr<HonestNumber>(s['honestNumbers']),
    landing: (typeof s['landing'] === 'object' && s['landing'] !== null
      ? (s['landing'] as LandingStats)
      : { ...EMPTY_LANDING }),
    treasury: (typeof s['treasury'] === 'object' && s['treasury'] !== null
      ? (s['treasury'] as Treasury)
      : { ...EMPTY_TREASURY }),
    latestAlerts: arr<AlertItem>(s['latestAlerts']),
    sweeps: arr<SweepRecord>(s['sweeps']),
    signalCounts24h: (typeof s['signalCounts24h'] === 'object' && s['signalCounts24h'] !== null
      ? (s['signalCounts24h'] as Record<string, number>)
      : {}),
    health: {
      lastTickTs: num(health['lastTickTs']),
      lastRefTs: num(health['lastRefTs']),
      lastSignalTs: num(health['lastSignalTs']),
      xPostsToday: num(health['xPostsToday']) ?? 0,
      postMode: health['postMode'] === 'live' ? 'live' : 'draft',
      dbBytes: num(health['dbBytes']),
    },
  };
}
